/**
 * Route Configuration - Simplified for Referrals-Only App
 * Centralized route definitions
 */

import { createElement, lazy, ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { PATHS } from '../constants/paths';
import type { RouteGroup, RouteConfig } from '../types';
import { isAuthenticated, getDefaultDashboardPath } from '../utils/auth';

// Lazy load pages
const LoginPage = lazy(() => import('../../pages/Login'));
const RegisterPage = lazy(() => import('../../pages/Register'));
const RegisterSuccessPage = lazy(() => import('../../pages/Register/RegisterSuccess'));
const NotFoundPage = lazy(() => import('../../pages/Errors/NotFound'));
const ForbiddenPage = lazy(() => import('../../pages/Errors/ForbiddenPage'));
const ServerErrorPage = lazy(() => import('../../pages/Errors/ServerErrorPage'));
const TermsOfServicePage = lazy(() => import('../../pages/Legal/TermsOfService'));
const PrivacyPolicyPage = lazy(() => import('../../pages/Legal/PrivacyPolicy'));
const ReferralsRouter = lazy(() => import('../../pages/Referrals/ReferralsRouter'));
const DashboardPage = lazy(() => import('../../pages/Dashboard/Dashboard'));
const AgencyProfilePage = lazy(() => import('../../pages/Agency/AgencyProfile'));
const CaregiverListPage = lazy(() => import('../../pages/Caregiver/CaregiverList'));
const CaregiverOnboardingPage = lazy(() => import('../../pages/Caregiver/CaregiverOnboarding'));
const CaregiverDetailPage = lazy(() => import('../../pages/Caregiver/CaregiverDetail'));
const CaregiverNewRedirect = lazy(() => import('../../pages/Caregiver/CaregiverNewRedirect'));
const StaffingBoardPage = lazy(() => import('../../pages/Staffing/StaffingBoard'));
const ServiceRequestsListPage = lazy(() => import('../../pages/ServiceRequests/ServiceRequestsList'));
const ServiceRequestDetailPage = lazy(() => import('../../pages/ServiceRequests/ServiceRequestDetail'));
const ServiceRequestCreatePage = lazy(() => import('../../pages/ServiceRequests/ServiceRequestCreate'));
const PatientsListPage = lazy(() => import('../../pages/Patients/PatientsList'));
const PatientsDetailPage = lazy(() => import('../../pages/Patients/PatientsDetail'));
const PatientHealthRecordsPage = lazy(() => import('../../pages/health-records'));
const ReviewsPage = lazy(() => import('../../pages/Reviews/Reviews'));
const TicketsPage = lazy(() => import('../../pages/Tickets/TicketsPage'));
const TicketDetailPage = lazy(() => import('../../pages/Tickets/TicketDetailPage'));
const CreateTicketPage = lazy(() => import('../../pages/Tickets/CreateTicketPage'));
const ForceResetPasswordPage = lazy(() => import('../../pages/ResetPassword/ForceResetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('../../pages/ForgotPassword/ForgotPasswordPage'));
const VerifyEmailPage = lazy(() => import('../../pages/VerifyEmail/VerifyEmailPage'));

/**
 * Root Redirect Component
 * Redirects users to login or dashboard based on authentication state
 */
const RootRedirect = (): ReactElement => {
  if (isAuthenticated()) {
    const dashboardPath = getDefaultDashboardPath();
    return createElement(Navigate, { to: dashboardPath, replace: true });
  }
  return createElement(Navigate, { to: PATHS.LOGIN, replace: true });
};

export const routes: RouteGroup = {
  public: [
    {
      path: PATHS.HOME,
      element: RootRedirect,
      guardType: 'public',
      meta: {
        title: 'UboraHealth - Home Health Management',
        description: 'Home Health Referral Management System',
      },
    },
    {
      path: PATHS.LOGIN,
      element: LoginPage,
      restricted: true, // Redirect to referrals if already authenticated
      guardType: 'public',
      meta: {
        title: 'Login - UboraHealth',
        description: 'Sign in to your home health referral management account',
      },
    },
    {
      path: PATHS.REGISTER,
      element: RegisterPage,
      restricted: true, // Redirect to referrals if already authenticated
      guardType: 'public',
      meta: {
        title: 'Register - UboraHealth',
        description: 'Create a new home health provider account',
      },
    },
    {
      path: PATHS.REGISTER_SUCCESS,
      element: RegisterSuccessPage,
      guardType: 'public',
      meta: {
        title: 'Registration Successful - UboraHealth',
        description: 'Your registration has been submitted for review',
      },
    },
    {
      path: PATHS.RESET_PASSWORD,
      element: ForceResetPasswordPage,
      guardType: 'public',
      meta: {
        title: 'Reset Password - UboraHealth',
        description: 'Reset your password to continue',
      },
    },
    {
      path: PATHS.FORGOT_PASSWORD,
      element: ForgotPasswordPage,
      guardType: 'public',
      meta: {
        title: 'Forgot Password - UboraHealth',
        description: 'Request a password reset link',
      },
    },
    {
      path: PATHS.VERIFY_EMAIL,
      element: VerifyEmailPage,
      guardType: 'public',
      meta: {
        title: 'Verify Email - UboraHealth',
        description: 'Verify your email address',
      },
    },
    {
      path: PATHS.NOT_FOUND,
      element: NotFoundPage,
      guardType: 'public',
      meta: {
        title: '404 - Page Not Found',
      },
    },
    {
      path: PATHS.FORBIDDEN,
      element: ForbiddenPage,
      guardType: 'public',
      meta: {
        title: '403 - Access Denied',
      },
    },
    {
      path: PATHS.SERVER_ERROR,
      element: ServerErrorPage,
      guardType: 'public',
      meta: {
        title: '500 - Server Error',
      },
    },
    {
      path: PATHS.TERMS_OF_SERVICE,
      element: TermsOfServicePage,
      guardType: 'public',
      meta: {
        title: 'Terms of Service',
        description: 'UboraHealth Terms of Service',
      },
    },
    {
      path: PATHS.TERMS_OF_USE,
      element: TermsOfServicePage,
      guardType: 'public',
      meta: {
        title: 'Terms of Use - UboraHealth',
        description: 'UboraHealth Terms of Use',
      },
    },
    {
      path: PATHS.PRIVACY_POLICY,
      element: PrivacyPolicyPage,
      guardType: 'public',
      meta: {
        title: 'Privacy Policy',
        description: 'UboraHealth Privacy Policy',
      },
    },
  ],

  protected: [
    {
      path: PATHS.DASHBOARD,
      element: DashboardPage,
      guardType: 'protected',
      meta: {
        title: 'Dashboard - UboraHealth',
        description: 'Home Health Referral Management Dashboard',
        requiresAuth: true,
      },
    },
    {
      path: '/referrals/*',
      element: ReferralsRouter,
      guardType: 'protected',
      meta: {
        title: 'Referrals',
        description: 'Manage home health referrals',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.REVIEWS,
      element: ReviewsPage,
      guardType: 'protected',
      meta: {
        title: 'Patient Reviews - UboraHealth',
        description: 'Read and respond to patient reviews',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.TICKETS,
      element: TicketsPage,
      guardType: 'protected',
      meta: {
        title: 'Help & Support - UboraHealth',
        description: 'Patient messages and support requests to admin',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.TICKETS_NEW,
      element: CreateTicketPage,
      guardType: 'protected',
      meta: {
        title: 'Contact Admin - UboraHealth',
        description: 'Send a message to the Ubora Health admin team',
        requiresAuth: true,
      },
    },
    {
      path: '/tickets/:id',
      element: TicketDetailPage,
      guardType: 'protected',
      meta: {
        title: 'Ticket Detail - UboraHealth',
        description: 'View and manage ticket',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.SERVICE_REQUESTS,
      element: ServiceRequestsListPage,
      guardType: 'protected',
      meta: {
        title: 'Care Requests - UboraHealth',
        description: 'Review and respond to patient care requests',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.SERVICE_REQUEST_NEW,
      element: ServiceRequestCreatePage,
      guardType: 'protected',
      meta: {
        title: 'Create Care Request - UboraHealth',
        description: 'Submit a new care request for a patient',
        requiresAuth: true,
      },
    },
    {
      path: '/service-requests/:id',
      element: ServiceRequestDetailPage,
      guardType: 'protected',
      meta: {
        title: 'Care Request Details - UboraHealth',
        description: 'Review and update a patient care request',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.PATIENTS,
      element: PatientsListPage,
      guardType: 'protected',
      meta: {
        title: 'Patients - UboraHealth',
        description: 'Manage patient records and demographics',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.PATIENT_DETAIL(':id'),
      element: PatientsDetailPage,
      guardType: 'protected',
      meta: {
        title: 'Patient Details - UboraHealth',
        description: 'View patient record details',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.PATIENT_HEALTH_RECORDS(':id'),
      element: PatientHealthRecordsPage,
      guardType: 'protected',
      meta: {
        title: 'Patient Health Records - UboraHealth',
        description: 'Review patient health records and history',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.AGENCY_ONBOARDING,
      element: AgencyProfilePage,
      guardType: 'protected',
      meta: {
        title: 'Agency Setup - UboraHealth',
        description: 'Complete your agency profile and get verified',
        requiresAuth: true,
      },
    },
    {
      path: '/agency/onboarding/step/:step',
      element: AgencyProfilePage,
      guardType: 'protected',
      meta: {
        title: 'Agency Setup - UboraHealth',
        description: 'Complete your agency profile and get verified',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.AGENCY_PROFILE,
      element: AgencyProfilePage,
      guardType: 'protected',
      meta: {
        title: 'Agency Setup - UboraHealth',
        description: 'Manage your agency profile and verification',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.CAREGIVERS_LIST,
      element: CaregiverListPage,
      guardType: 'protected',
      meta: {
        title: 'Staff & Caregivers - UboraHealth',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.STAFFING,
      element: StaffingBoardPage,
      guardType: 'protected',
      meta: {
        title: 'Staffing Board - UboraHealth',
        requiresAuth: true,
      },
    },
    {
      path: PATHS.CAREGIVER_NEW,
      element: CaregiverNewRedirect,
      guardType: 'protected',
      meta: {
        title: 'Add New Caregiver - UboraHealth',
        requiresAuth: true,
      },
    },
    {
      path: '/caregivers/type/:type',
      element: CaregiverListPage,
      guardType: 'protected',
      meta: {
        title: 'Staff by Discipline - UboraHealth',
        requiresAuth: true,
      },
    },
    {
      path: '/caregivers/:id',
      element: CaregiverDetailPage,
      guardType: 'protected',
      meta: {
        title: 'Caregiver Profile - UboraHealth',
        requiresAuth: true,
      },
    },
    {
      path: '/caregivers/:id/edit',
      element: CaregiverOnboardingPage,
      guardType: 'protected',
      meta: {
        title: 'Edit Caregiver Profile - UboraHealth',
        requiresAuth: true,
      },
    },
    {
      path: '/caregiver/onboarding/:id/step/:step',
      element: CaregiverOnboardingPage,
      guardType: 'protected',
      meta: {
        title: 'Caregiver Onboarding - UboraHealth',
        requiresAuth: true,
      },
    },
  ],

  admin: [],
};

/**
 * Get all routes as a flat array
 * Useful for rendering routes without duplication
 */
export const getAllRoutes = (): RouteConfig[] => {
  return [...routes.public, ...routes.protected, ...(routes.admin || [])];
};
